import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Overlay } from "ol";
import { useAppContext } from "../../Contexts/AppContext";
import { Popup, PopupHeader, PopupBody, PopupFooter } from "../../Popups/Popup";
import { getMap } from "../GeoLiteMap";
import { PopupHeaderButton } from "../../Popups/PopupHeaderButton";
import i18next from "i18next";
import { useIdentifyContext } from "../../Contexts/IdentifyContext";
import { IdentifyResults } from "./IdentifyResults";
import { IdentifyResultsArray } from "./identifyUtils";
import { useMapContext } from "../../Contexts/MapContext";
import "./Identify.scss";

export const IdentifyPopup = (): JSX.Element => {
    const { highlightFeatures, setHighlightFeatures, setLocateFeature, setShowToast, isMobile } = useAppContext();
    const { featuresPopup, setFeaturesPopup } = useMapContext();
    const { identifyResults, setResults, coordinates, coordinatesString } = useIdentifyContext();
    const [dragged, setDragged] = useState<boolean>(false);

    const isIFrame = iniFrame();

    const overlayRef = useRef<Overlay>();

    useEffect(() => {
        if (isIFrame || isMobile) {
            return;
        }
        overlayRef.current = new Overlay({
            element: document.getElementById("identify"),
            offset: [0, -35],
            positioning: "bottom-center",
            className: "popup-overlay"
        });
        getMap().addOverlay(overlayRef.current);
    }, []);

    useEffect(() => {
        if (!isIFrame && !isMobile && identifyResults && coordinates && identifyResults.length) {
            overlayRef.current.setPosition(coordinates);
            overlayRef.current.panIntoView({ animation: { duration: 250 } });
        }
        highlightResult(identifyResults);
        setDragged(false);
    }, [identifyResults, featuresPopup]);

    const highlightResult = useCallback(
        (results: IdentifyResultsArray) => {
            results && results.length && results[0][1][0].feature && !featuresPopup?.hideHighLight
                ? setHighlightFeatures([results[0][1][0].feature])
                : setHighlightFeatures(null);
        },
        [featuresPopup]
    );

    const onClose = () => {
        setResults(null);
        setHighlightFeatures(null);
        setFeaturesPopup(null);
    };

    const onDrag = () => setDragged(true);

    const onCopy = useCallback(
        (params) => {
            if (!params || !params.position || !params.position.length) {
                return;
            }
            navigator.clipboard.writeText(coordinatesString);
            setShowToast({
                position: [params.position[0] - params.contentWidth / 2 - 24, params.position[1] - 60],
                title: i18next.t("coordinateConversion.copySuccess"),
                text: coordinatesString
            });
        },
        [coordinatesString]
    );

    const onZoom = useCallback(() => {
        highlightFeatures.features &&
            highlightFeatures.features[0].getGeometry() &&
            setLocateFeature(highlightFeatures.features[0]);
    }, [highlightFeatures]);

    return (
        <div id="identify">
            {identifyResults && identifyResults.length ? (
                <>
                    <Popup id={"identify"} sameContainer={false} isMobile={false} onDrag={onDrag}>
                        {/* TODO fix mobile version to use actual iMobile parameter */}
                        {/* Draggable component blocks Click events on mobile, on drag handler element (header)
                        and as result, Close and Zoom buttons does not work */}
                        {isMobile ? (
                            <>
                                <PopupHeaderButton
                                    className="zoom-mobile"
                                    iconClassName={"icon-geolite-zoom"}
                                    title={i18next.t("zoomIn")}
                                    onClick={onZoom}
                                />

                                <PopupHeaderButton
                                    className="close-mobile"
                                    iconClassName={"icon-geolite-close"}
                                    title={i18next.t("close")}
                                    onClick={onClose}
                                />
                            </>
                        ) : null}
                        <PopupHeader isMobile={false}>
                            {!isMobile ? (
                                <>
                                    <PopupHeaderButton
                                        iconClassName={"icon-geolite-zoom"}
                                        title={i18next.t("zoomIn")}
                                        onClick={onZoom}
                                    />

                                    <PopupHeaderButton
                                        iconClassName={"icon-geolite-close"}
                                        title={i18next.t("close")}
                                        onClick={onClose}
                                    />
                                </>
                            ) : null}
                        </PopupHeader>
                        <PopupBody isMobile={false}>
                            <IdentifyResults results={identifyResults} />
                        </PopupBody>
                        <PopupFooter isMobile={false} onClick={onCopy}>
                            <div className="identified-coordinates">{coordinatesString}</div>
                            <div className="copy-button">
                                <span className="icon-geolite-copy"></span>
                            </div>
                        </PopupFooter>
                        {!isIFrame && !dragged && !featuresPopup?.hideArrow ? (
                            <div className="bottom-triangle" />
                        ) : null}
                    </Popup>
                </>
            ) : null}
        </div>
    );
};

const iniFrame = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};
